import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Download,
  RefreshCw,
  DollarSign,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Filter,
  Store
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface ShopifyStoreInfo {
  storeId: string;
  storeName: string;
  isActive: boolean;
}

interface PayoutSummary {
  adjustments_fee_amount: string;
  adjustments_gross_amount: string;
  charges_fee_amount: string;
  charges_gross_amount: string;
  refunds_fee_amount: string;
  refunds_gross_amount: string;
  reserved_funds_fee_amount: string;
  reserved_funds_gross_amount: string;
  retried_payouts_fee_amount: string;
  retried_payouts_gross_amount: string;
}

interface Payout {
  id: number;
  status: 'scheduled' | 'in_transit' | 'paid' | 'failed' | 'canceled';
  date: string;
  currency: string;
  amount: string;
  summary: PayoutSummary;
  storeId?: string;
  storeName?: string;
}

interface BalanceTransaction {
  id: number;
  type: string;
  amount: string;
  fee: string;
  net: string;
  currency: string;
  source_order_id: number | null;
  processed_at: string;
}

interface PayoutExport {
  payout: Payout;
  summary: {
    payoutId: number;
    payoutDate: string;
    payoutAmount: string;
    currency: string;
    status: string;
    transactionCount: number;
    orderCount: number;
    lineItemCount: number;
  };
  transactions: BalanceTransaction[];
  lineItems: any[];
  storeId?: string;
  storeName?: string;
}

interface StoreBalance {
  storeId: string;
  storeName: string;
  balance: { amount: string; currency: string }[];
}

const ShopifyPayouts: React.FC = () => {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [exportingPayoutKey, setExportingPayoutKey] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedPayout, setSelectedPayout] = useState<number | null>(null);
  const [payoutDetails, setPayoutDetails] = useState<PayoutExport | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [balances, setBalances] = useState<StoreBalance[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('paid');
  const [storeFilter, setStoreFilter] = useState<string>('all');
  const [availableStores, setAvailableStores] = useState<ShopifyStoreInfo[]>([]);

  // Set default dates (30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  // Fetch stores and balance on mount
  useEffect(() => {
    fetchStores();
    fetchBalance();
  }, []);

  // Re-fetch balance when store filter changes
  useEffect(() => {
    fetchBalance();
  }, [storeFilter]);

  const getPayoutExportKey = (payoutId: number, storeId?: string) => `${storeId || 'all'}-${payoutId}`;

  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/shopify/payouts/stores`);
      if (response.ok) {
        const data = await response.json();
        setAvailableStores(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchBalance = async () => {
    try {
      const params = new URLSearchParams();
      if (storeFilter !== 'all') params.append('store', storeFilter);

      const response = await fetch(`${API_BASE}/api/shopify/payouts/balance?${params}`);
      if (response.ok) {
        const data = await response.json();
        setBalances(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchPayouts = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        dateMin: startDate,
        dateMax: endDate,
        store: storeFilter,
      });
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${API_BASE}/api/shopify/payouts?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPayouts(data.data || []);
        toast.success(`Found ${data.count} payouts`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to fetch payouts');
      }
    } catch (error) {
      toast.error('Error fetching payouts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayoutDetails = async (payoutId: number, storeId?: string) => {
    if (selectedPayout === payoutId) {
      setSelectedPayout(null);
      setPayoutDetails(null);
      return;
    }

    setSelectedPayout(payoutId);
    setLoadingDetails(true);
    try {
      const params = new URLSearchParams();
      if (storeId) params.append('store', storeId);

      const response = await fetch(`${API_BASE}/api/shopify/payouts/${payoutId}/export?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPayoutDetails(data.data);
      } else {
        toast.error('Failed to fetch payout details');
      }
    } catch (error) {
      toast.error('Error fetching payout details');
      console.error(error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const downloadCsv = async (payoutId: number, payoutDate: string, storeId?: string) => {
    const exportKey = getPayoutExportKey(payoutId, storeId);
    setExportingPayoutKey(exportKey);

    try {
      const params = new URLSearchParams();
      if (storeId) params.append('store', storeId);

      const response = await fetch(`${API_BASE}/api/shopify/payouts/${payoutId}/csv?${params}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payout_${payoutDate}_${payoutId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Downloaded payout CSV');
      } else {
        toast.error('Failed to download CSV');
      }
    } catch (error) {
      toast.error('Error downloading CSV');
      console.error(error);
    } finally {
      setExportingPayoutKey(null);
    }
  };

  const exportDateRange = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (payouts.length === 0) {
      toast.error('Please search for payouts first');
      return;
    }

    setExportingAll(true);
    try {
      const response = await fetch(`${API_BASE}/api/shopify/payouts/export/csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate,
          endDate,
          store: storeFilter,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        toast.error(error?.error || 'Failed to export payouts');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shopify_payouts_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Exported ${payouts.length} payouts`);
    } catch (error) {
      toast.error('Error exporting payouts');
      console.error(error);
    } finally {
      setExportingAll(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'in_transit':
        return 'bg-blue-100 text-blue-800';
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStoreColor = (storeId?: string) => {
    if (storeId === 'shopify_2') return 'bg-purple-100 text-purple-800';
    return 'bg-indigo-100 text-indigo-800';
  };

  const formatCurrency = (amount: string, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(parseFloat(amount));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Shopify Payouts Export</h1>
        <p className="text-gray-600">Export payout transactions with line items for QuickBooks categorization</p>
      </div>

      {/* Balance Cards */}
      {balances.length > 0 && (
        <div className={`grid grid-cols-1 ${balances.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
          {balances.map((sb) => (
            <div
              key={sb.storeId}
              className={`card text-white ${
                sb.storeId === 'shopify_2'
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">{sb.storeName}</p>
                  {sb.balance.length > 0 ? (
                    <>
                      <p className="text-2xl font-bold">
                        {formatCurrency(sb.balance[0].amount, sb.balance[0].currency)}
                      </p>
                      <p className="text-sm opacity-80">Pending payout</p>
                    </>
                  ) : (
                    <p className="text-lg font-medium">No balance data</p>
                  )}
                </div>
                <DollarSign className="h-12 w-12 opacity-40" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Search Payouts</h3>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          {/* Store Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Store
            </label>
            <div className="relative">
              <Store className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                className="input pl-10"
                disabled={loading || exportingAll}
              >
                <option value="all">All Stores</option>
                {availableStores.map((s) => (
                  <option key={s.storeId} value={s.storeId}>
                    {s.storeName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input pl-10"
                disabled={loading || exportingAll}
              />
            </div>
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input pl-10"
                disabled={loading || exportingAll}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input pl-10"
                disabled={loading || exportingAll}
              >
                <option value="">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="in_transit">In Transit</option>
                <option value="scheduled">Scheduled</option>
                <option value="failed">Failed</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-end space-x-2">
            <button
              onClick={fetchPayouts}
              disabled={loading || exportingAll || !startDate || !endDate}
              className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Search
            </button>
            <button
              onClick={exportDateRange}
              disabled={loading || exportingAll || !startDate || !endDate}
              className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingAll ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {exportingAll ? 'Generating CSV...' : 'Export All'}
            </button>
          </div>
        </div>

        {/* Date Range Info */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Date Range:</strong> {startDate} to {endDate}
            {startDate && endDate && (
              <span className="ml-2">
                ({Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))} days)
              </span>
            )}
            {storeFilter !== 'all' && (
              <span className="ml-2">
                • <strong>Store:</strong> {availableStores.find(s => s.storeId === storeFilter)?.storeName || storeFilter}
              </span>
            )}
          </p>
          {exportingAll && (
            <p className="text-sm text-blue-600 mt-2">
              Generating the CSV for this date range. Large payout exports can take a minute.
            </p>
          )}
        </div>
      </div>

      {/* Payouts List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Payouts ({payouts.length})
          </h3>
        </div>

        {payouts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <DollarSign className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No payouts found</p>
            <p className="text-sm">Search for payouts to see them here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payouts.map((payout) => (
              <div
                key={`${payout.storeId}-${payout.id}`}
                className="border rounded-lg overflow-hidden"
              >
                {/* Payout Header */}
                <div
                  className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => fetchPayoutDetails(payout.id, payout.storeId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {selectedPayout === payout.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900">
                            Payout #{payout.id}
                          </p>
                          {payout.storeName && (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStoreColor(payout.storeId)}`}>
                              {payout.storeName.replace(' Store', '')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {new Date(payout.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payout.status)}`}>
                        {payout.status}
                      </span>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatCurrency(payout.amount, payout.currency)}
                      </p>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Charges:</span>
                      <span className="ml-2 font-medium text-green-600">
                        +{formatCurrency(payout.summary.charges_gross_amount, payout.currency)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Refunds:</span>
                      <span className="ml-2 font-medium text-red-600">
                        -{formatCurrency(payout.summary.refunds_gross_amount, payout.currency)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Fees:</span>
                      <span className="ml-2 font-medium text-gray-600">
                        -{formatCurrency(payout.summary.charges_fee_amount, payout.currency)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedPayout === payout.id && (
                  <div className="p-4 border-t">
                    {loadingDetails ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600">Loading payout details...</span>
                      </div>
                    ) : payoutDetails ? (
                      <div className="space-y-4">
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-600">Transactions</p>
                            <p className="text-xl font-bold text-blue-900">
                              {payoutDetails.summary.transactionCount}
                            </p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-sm text-green-600">Orders</p>
                            <p className="text-xl font-bold text-green-900">
                              {payoutDetails.summary.orderCount}
                            </p>
                          </div>
                          <div className="p-3 bg-purple-50 rounded-lg">
                            <p className="text-sm text-purple-600">Line Items</p>
                            <p className="text-xl font-bold text-purple-900">
                              {payoutDetails.summary.lineItemCount}
                            </p>
                          </div>
                        </div>

                        {/* Transaction Breakdown */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Transaction Types</h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-2 px-3">Type</th>
                                  <th className="text-right py-2 px-3">Count</th>
                                  <th className="text-right py-2 px-3">Gross</th>
                                  <th className="text-right py-2 px-3">Fees</th>
                                  <th className="text-right py-2 px-3">Net</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(
                                  payoutDetails.transactions.reduce((acc, tx) => {
                                    if (!acc[tx.type]) {
                                      acc[tx.type] = { count: 0, gross: 0, fees: 0, net: 0 };
                                    }
                                    acc[tx.type].count++;
                                    acc[tx.type].gross += parseFloat(tx.amount);
                                    acc[tx.type].fees += parseFloat(tx.fee);
                                    acc[tx.type].net += parseFloat(tx.net);
                                    return acc;
                                  }, {} as Record<string, { count: number; gross: number; fees: number; net: number }>)
                                ).map(([type, data]) => (
                                  <tr key={type} className="border-b">
                                    <td className="py-2 px-3 capitalize">{type}</td>
                                    <td className="py-2 px-3 text-right">{data.count}</td>
                                    <td className="py-2 px-3 text-right">
                                      {formatCurrency(data.gross.toString(), payoutDetails.payout.currency)}
                                    </td>
                                    <td className="py-2 px-3 text-right text-red-600">
                                      {formatCurrency(data.fees.toString(), payoutDetails.payout.currency)}
                                    </td>
                                    <td className="py-2 px-3 text-right font-medium">
                                      {formatCurrency(data.net.toString(), payoutDetails.payout.currency)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Download Button */}
                        <div className="flex space-x-3 pt-2">
                          {(() => {
                            const payoutExportKey = getPayoutExportKey(payout.id, payout.storeId);
                            const isExportingThisPayout = exportingPayoutKey === payoutExportKey;

                            return (
                          <button
                            onClick={() => downloadCsv(payout.id, payout.date, payout.storeId)}
                            disabled={isExportingThisPayout || exportingAll}
                            className="btn-primary flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isExportingThisPayout ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            {isExportingThisPayout ? 'Generating CSV...' : 'Export CSV for QuickBooks'}
                          </button>
                            );
                          })()}
                        </div>

                        {/* Sample Line Items */}
                        {payoutDetails.lineItems.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">
                              Sample Line Items (first 5)
                            </h4>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-gray-50">
                                    <th className="text-left py-2 px-3">Order</th>
                                    <th className="text-left py-2 px-3">Product</th>
                                    <th className="text-left py-2 px-3">SKU</th>
                                    <th className="text-right py-2 px-3">Qty</th>
                                    <th className="text-right py-2 px-3">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {payoutDetails.lineItems.slice(0, 5).map((item, idx) => (
                                    <tr key={idx} className="border-b">
                                      <td className="py-2 px-3">{item.order_name}</td>
                                      <td className="py-2 px-3">{item.title}</td>
                                      <td className="py-2 px-3 text-gray-500">{item.sku || '-'}</td>
                                      <td className="py-2 px-3 text-right">{item.quantity}</td>
                                      <td className="py-2 px-3 text-right">
                                        {formatCurrency(item.line_total, payoutDetails.payout.currency)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Info */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <ExternalLink className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">QuickBooks Integration</h4>
            <p className="text-sm text-blue-700 mt-1">
              The Line Items CSV includes product details, SKUs, vendors, and product types
              that can be used to categorize sales in QuickBooks. Each row represents a
              single line item from an order, with the payout it was included in.
            </p>
            <p className="text-sm text-blue-700 mt-2">
              <strong>CSV Columns:</strong> store, payout_id, payout_date, order_name, customer_email,
              sku, title, vendor, product_type, quantity, unit_price, line_total, and more.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopifyPayouts;
